import type { ComponentProps } from "react"
import Separator from "@/v2-ui/separator"
import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import { TooltipLink } from "@/v2-backoffice-client/components/link"
import UserUpdateButton from "@/v2-backoffice-client/user/update/UserUpdateButton"
import UserCustomerUpdateSelector from "@/v2-backoffice-client/user/customer/update/UserCustomerUpdateSelector"
import UserResetPasswordButton from "@/v2-backoffice-client/user/resetPassword/UserResetPasswordButton"
import UserResetMfaButton from "@/v2-backoffice-client/user/resetMfa/UserResetMfaButton"
import UserRevokeSessionsButton from "@/v2-backoffice-client/user/revokeSessions/UserRevokeSessionsButton"
import UserStatusUpdateButton from "@/v2-backoffice-client/user/status/update/UserStatusUpdateButton"
import UserSharingFlagButton from "@/v2-backoffice-client/user/sharingFlag/UserSharingFlagButton"
import UserMultiSessionButton from "@/v2-backoffice-client/user/multiSession/UserMultiSessionButton"
import {
  USER_STATUS_ACTIVE,
  USER_STATUS_BLOCKED,
  USER_STATUS_REMOVED,
} from "@/v2-backoffice-client/user/status/userStatus.constants"
import { getUserDirectoryPortalUrl } from "@/v2-backoffice-client/user/UserEntraButton"
import UserHubspotButton from "@/v2-backoffice-client/user/UserHubspotButton"
import { buildMixpanelUserActivityUrl } from "@/v2-backoffice-client/user/view/user.view.utils"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"

export type UserViewToolbarProps = ComponentProps<"div"> & {
  user: UserFragment | null | undefined
}

function UserViewToolbar(props: UserViewToolbarProps) {
  const { user, ...viewSheetToolbarProps } = props

  if (!user) return null

  const isRemoved = user.UserStatusCode === USER_STATUS_REMOVED

  return (
    <ViewSheet.Toolbar {...viewSheetToolbarProps}>
      <TooltipLink
        to={user.Email ? buildMixpanelUserActivityUrl(user.Email) : undefined}
        variant="ghost"
        isStacked
        iconSrc="mixpanel"
        content="Open in Mixpanel"
        disabled={!user.Email}
      >
        Mixpanel
      </TooltipLink>

      <TooltipLink
        to={user.AzureID ? getUserDirectoryPortalUrl(user) : undefined}
        variant="ghost"
        isStacked
        iconSrc="entra"
        content="Open in the directory that owns this user's sign-in"
        disabled={!user.AzureID}
      >
        Entra
      </TooltipLink>

      <UserHubspotButton
        externalContactId={user.Contact?.ExternalContactId}
        isStacked
      >
        HubSpot
      </UserHubspotButton>

      <Separator orientation="vertical" />

      <UserUpdateButton user={user} isStacked>
        Update
      </UserUpdateButton>

      <UserCustomerUpdateSelector
        user={user}
        variant="ghost"
        isStacked
        content="Update the customer that owns this user: whose licenses and seats apply to them. Normally the customer of the user's teams."
      >
        Customer
      </UserCustomerUpdateSelector>

      <UserResetPasswordButton userId={user.Id} isStacked>
        Password
      </UserResetPasswordButton>

      <UserResetMfaButton userId={user.Id} isStacked>
        Reset MFA
      </UserResetMfaButton>

      <Separator orientation="vertical" />

      <UserRevokeSessionsButton userId={user.Id} variant="warning" isStacked>
        Revoke
      </UserRevokeSessionsButton>

      {user.UserStatusCode !== USER_STATUS_ACTIVE && (
        <UserStatusUpdateButton
          userId={user.Id}
          statusCode={USER_STATUS_ACTIVE}
          isStacked
        />
      )}

      {user.UserStatusCode !== USER_STATUS_BLOCKED && (
        <UserStatusUpdateButton
          userId={user.Id}
          statusCode={USER_STATUS_BLOCKED}
          isStacked
        />
      )}

      <UserSharingFlagButton userId={user.Id} isStacked />

      <UserMultiSessionButton
        userId={user.Id}
        isAllowed={Boolean(user.MultiSessionAllowed)}
        isStacked
      />

      {!isRemoved && <Separator orientation="vertical" />}
      {!isRemoved && (
        <UserStatusUpdateButton
          userId={user.Id}
          statusCode={USER_STATUS_REMOVED}
          isStacked
        />
      )}
    </ViewSheet.Toolbar>
  )
}

export default UserViewToolbar
